import { Box } from '@mui/material';
import { ReactNode } from 'react';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { READOUT, READOUT_DIM, READOUT_FONT } from '../chrome';
import { tileFrameSx } from './tileFrame';
import TileTitleBar, { TITLE_BAR_DELIMITER_SX } from './TileTitleBar';

interface PanelTileProps {
    /** The panel's name, as read in the title bar. */
    readonly title: string;
    /**
     * What the panel reads, named beside the title. May carry PHI, so it
     * is masked with the privacy toggle.
     */
    readonly subject?: string;
    /** Controls the title bar offers beside maximize. */
    readonly actions?: ReactNode;
    readonly isActive?: boolean;
    readonly isMaximized?: boolean;
    readonly onMaximize?: () => void;
    /** Pointer listeners for the title bar (the rearrange handle). */
    readonly dragHandleProps?: Record<string, unknown>;
    /** The panel itself. */
    readonly children: ReactNode;
}

/**
 * A console tile that hosts a panel rather than an image: the same frame
 * and title bar as an image tile, named after the panel and what it reads,
 * with the panel filling the pane under the bar. Nothing here reads the
 * engine; the panel brings its own data.
 */
export default function PanelTile({
    title,
    subject,
    actions,
    isActive = false,
    isMaximized = false,
    onMaximize,
    dragHandleProps,
    children,
}: PanelTileProps) {
    return (
        <Box
            data-testid="panel-tile"
            role="group"
            aria-label={title}
            sx={tileFrameSx(isActive)}
        >
            <TileTitleBar
                controls={actions}
                isMaximized={isMaximized}
                onMaximize={onMaximize}
                dragHandleProps={dragHandleProps}
            >
                <Box
                    component="span"
                    data-testid="panel-title"
                    sx={{
                        color: READOUT,
                        fontFamily: READOUT_FONT,
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        flexShrink: 0,
                    }}
                >
                    {title}
                </Box>
                {subject ? (
                    <>
                        <Box sx={TITLE_BAR_DELIMITER_SX} />
                        <Box
                            component="span"
                            data-testid="panel-subject"
                            sx={{
                                color: READOUT_DIM,
                                fontFamily: READOUT_FONT,
                                fontSize: 11,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <SensitiveInfoTypography>
                                {subject}
                            </SensitiveInfoTypography>
                        </Box>
                    </>
                ) : null}
                <Box sx={{ flex: 1 }} />
            </TileTitleBar>
            <Box
                data-testid="panel-tile-body"
                sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
